import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import { ButtonandLeftBarColor, buttonFontColor } from "../../constants/styleConstant";

/**
 * Shown in place of a route whose code could not be downloaded because the
 * browser is offline.
 *
 * This component is deliberately part of the MAIN bundle, not a lazy chunk —
 * a chunk is exactly what cannot be fetched in the situation it exists for.
 *
 * It replaces only the route's own area. The layout, the left nav and whatever
 * the agent had open elsewhere stay on screen, which is the point: the previous
 * behaviour was `window.location.reload()`, and a reload with no network hands
 * the tab to the browser's own error page and loses everything.
 */
const OfflineRoute = () => {
  const [online, setOnline] = useState(() => navigator.onLine !== false);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1.5,
        p: 4,
        minHeight: 320,
      }}
    >
      <WifiOffIcon sx={{ fontSize: 48, color: "text.disabled" }} />
      <Typography variant="h6">This page could not be loaded</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
        {online
          ? "The connection came back. Reload to open this page."
          : "You appear to be offline. Anything already open is still here — this page just needs a connection to load."}
      </Typography>
      <Button
        variant="contained"
        disabled={!online}
        onClick={() => window.location.reload()}
        sx={{ mt: 1, color: buttonFontColor, bgcolor: ButtonandLeftBarColor }}
      >
        {online ? "Reload" : "Waiting for a connection…"}
      </Button>
    </Box>
  );
};

export default OfflineRoute;
